/**
 * Centralized ARM base URL configuration.
 *
 * In production the portal calls Azure Resource Manager directly (bearer token
 * auth is always included for non-localhost endpoints).
 *
 * In E2E test mode (`VITE_E2E_MODE=true`), ARM calls are redirected to the mock
 * control plane service at `http://localhost:4400` by default.
 *
 * In local development mode (`VITE_LOCAL_MODE=true`), ARM calls go to real Azure
 * since auth is always real MSAL. Override `VITE_ARM_BASE_URL` to use a mock
 * service if needed (e.g. for offline development).
 */

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';

const DEFAULT_MOCK_ARM_URL = 'http://localhost:4400';

function normalizeBaseUrl(url: string): string {
  // Trim any trailing slashes to avoid double slashes when composing paths.
  return url.replace(/\/+$/, '');
}

export function getArmBaseUrl(): string {
  if (import.meta.env.VITE_ARM_BASE_URL) {
    return normalizeBaseUrl(import.meta.env.VITE_ARM_BASE_URL);
  }
  if (isE2EMode) {
    return normalizeBaseUrl(DEFAULT_MOCK_ARM_URL);
  }
  return normalizeBaseUrl('https://management.azure.com');
}

/**
 * True when ARM calls target a local mock service (no real Azure auth needed).
 *
 * Mock mode activates when the resolved base URL is localhost — this happens in
 * E2E mode (automatic) or when `VITE_ARM_BASE_URL` is explicitly set to a
 * localhost address (manual opt-in for offline development).
 */
export function isArmMockMode(): boolean {
  const baseUrl = getArmBaseUrl();
  return (
    baseUrl.startsWith('http://localhost') || baseUrl.startsWith('http://127.0.0.1')
  );
}
